'use client';
import React, { useEffect } from 'react';
import { AuthContainerV2 } from '@benzinga/auth-ui';
import { Button } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { SubscriptionsManager } from '@benzinga/subscription-manager';
import { useIsUserLoggedIn, usePermission } from '@benzinga/user-context';

import styles from './styles.module.scss';
import { useSearchParams } from 'next/navigation';

interface LoginPageProps {
  referer: string | null;
}
export type LoginRegisterEventsTypes =
  | 'close'
  | 'already_logged_in'
  | 'login_succeed'
  | 'login_failed'
  | 'register_succeed'
  | 'start_trial_succeed'
  | 'register_failed';

const LoginHandler = ({ referer }: LoginPageProps) => {
  const session = React.useContext(SessionContext);
  const isLoggedIn = useIsUserLoggedIn();

  const [subscriptions, setSubscriptions] = React.useState<{ link: string; name: string }[] | null>(null);
  const isEditor = usePermission('editorial/edit');
  const isBenzingaContributor = usePermission('cms/role');
  const isAdmin = usePermission('#', '#');
  const searchParams = useSearchParams();
  const authMode: 'login' | 'register' = searchParams?.get('action') === 'register' ? 'register' : 'login';

  useEffect(() => {
    try {
      if (isEditor) {
        window.localStorage.setItem('editorial/edit', '1');
      }
      if (isBenzingaContributor) {
        window.localStorage.setItem('cms/role_contributor', '1');
      }
    } catch {
      // Ignore localStorage failures (private mode / quota)
    }
  }, [isEditor, isBenzingaContributor]);

  // benzinga-newsletter, benzinga-premium
  const loadSubscriptions = React.useCallback(() => {
    const subs: { link: string; name: string }[] = [];
    // This does not load subscriptions because for some reason they not loaded on login
    const subscriptions = session.getManager(SubscriptionsManager).getSubscriptionsStored();
    if (subscriptions?.length) {
      if (subscriptions.filter(sub => sub.product === 'benzinga-pro')) {
        subs.push({ link: 'https://pro.benzinga.com/dashboard/', name: 'Benzinga Pro' });
      } else {
        subs.push({ link: 'https://www.benzinga.com/pro/register?utm=login_list', name: 'Benzinga Pro Trial' });
      }
      subs.push({ link: 'https://www.benzinga.com/research', name: 'Benzinga Research' });
    }
    if (isEditor) {
      subs.push({ link: 'https://www.benzinga.com/admin/bz/editor/queue', name: 'Benzinga Admin/Editorial' });
      subs.push({ link: 'https://www.benzinga.com/node/add/story', name: 'Create Story' });
    }
    if (isAdmin) {
      subs.push({ link: 'https://www.benzinga.com/admin/bz/api', name: 'API Admin' });
    }
    setSubscriptions(subs);
    return subs;
  }, [isEditor, isAdmin, session]);

  const handleGoToPortfolio = React.useCallback(() => {
    window.location.href = '/profile/portfolio/';
  }, []);

  const handleGoToSubscription = React.useCallback(subscription => {
    if (subscription.link) {
      window.location.href = subscription.link;
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      (async () => {
        await loadSubscriptions();
      })();
    }
  }, [isLoggedIn, loadSubscriptions]);

  return (
    <>
      {subscriptions?.length ? (
        <div className={styles.subscriptionBox}>
          <h2>Access Your Subscription</h2>
          <div className="p-2">
            <Button as="a" className="w-full" href="https://www.benzinga.com">
              Benzinga.com
            </Button>
          </div>
          {subscriptions.map(subscription => {
            return (
              <div className="p-2" key={subscription?.name}>
                <Button className="w-full" onClick={() => handleGoToSubscription(subscription)} variant="primary">
                  {subscription?.name}
                </Button>
              </div>
            );
          })}
          <div className="p-2">
            <Button className="w-full" onClick={handleGoToPortfolio} variant="default">
              Go To My Portfolio
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.authBox}>
          <AuthContainerV2
            fallbackReturnUrl={referer ?? undefined}
            initialMode={authMode}
            placement="login"
            surface="page"
          />
        </div>
      )}
    </>
  );
};    

export default LoginHandler;
